import KnowledgeServiceAPI from 'shared/api/knowledge.service.api';

/**
 * Upload file to Google Cloud Storage (customer assets bucket) using the assets service signed URLs.
 *
 * @param {Object} params
 * @param {File} params.file - The file to upload
 * @param {string} params.name - The file name to store
 * @returns {Promise<{url: string, assetId: string, fileUrl: string}>}
 */
export const uploadToGCS = async ({ file, name }) => {
  if (!file) throw new Error('No file provided');
  if (file.size < 1) throw new Error('File is empty');

  try {
    const { data: signedUrlData } = await KnowledgeServiceAPI.getUploadUrl({
      type: file.type,
      size: file.size,
      name: file.name,
    });

    await KnowledgeServiceAPI.uploadDirectlyToGCS(signedUrlData.url, file);

    return {
      url: name,
      assetId: signedUrlData.id,
      fileUrl: signedUrlData.fileUrl,
    };
  } catch (error) {
    console.error('Error uploading to GCS:', error);
    throw new Error(`Failed to upload file: ${error.message}`);
  }
};

/**
 * Upload a logo to the logos bucket (headtotoe-dev-app-assets) via the dedicated
 * /actions/upload-logo endpoint. The file is stored at `logos/{subfolder}/{fileName}`.
 *
 * @param {Object} params
 * @param {File} params.file - The file to upload
 * @param {string} [params.subfolder='web'] - Subfolder within logos/ ('web' or 'hub')
 * @param {string} [params.fileName] - Optional fixed filename (used by hub logo to reuse org logo name)
 * @returns {Promise<{fileName: string}>} - The filename stored in GCS
 */
export const uploadLogoToGCS = async ({ file, subfolder = 'web', fileName }) => {
  if (!file) throw new Error('No file provided');
  if (file.size < 1) throw new Error('File is empty');

  try {
    const { data } = await KnowledgeServiceAPI.getLogoUploadUrl({
      type: file.type,
      size: file.size,
      name: file.name,
      subfolder,
      fileName,
    });

    await KnowledgeServiceAPI.uploadDirectlyToGCS(data.url, file);

    return { fileName: data.fileName };
  } catch (error) {
    console.error('Error uploading logo to GCS:', error);
    throw new Error(`Failed to upload logo: ${error.message}`);
  }
};
